import React, { useRef, useState, useEffect } from "react";
import { Box, TextField, alpha, useTheme } from "@mui/material";

interface OTPInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
}

export const OTPInput = ({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled = false,
  error = false,
}: OTPInputProps) => {
  const theme = useTheme();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [localValues, setLocalValues] = useState<string[]>(
    Array(length).fill("")
  );

  // Sync with external value
  useEffect(() => {
    const chars = value.split("").slice(0, length);
    const newValues = Array(length).fill("");
    chars.forEach((char, idx) => {
      newValues[idx] = char;
    });
    setLocalValues(newValues);
  }, [value, length]);

  const focusInput = (index: number) => {
    if (index >= 0 && index < length && inputRefs.current[index]) {
      inputRefs.current[index]?.focus();
    }
  };

  const handleChange = (index: number, inputValue: string) => {
    // Only allow single digit
    const digit = inputValue.replace(/\D/g, "").slice(-1);

    const newValues = [...localValues];
    newValues[index] = digit;
    setLocalValues(newValues);

    const fullValue = newValues.join("");
    onChange(fullValue);

    // Move to next input if digit was entered
    if (digit && index < length - 1) {
      focusInput(index + 1);
    }

    // Check if complete
    if (digit && index === length - 1) {
      const completeValue = newValues.join("");
      if (completeValue.length === length) {
        onComplete?.(completeValue);
      }
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newValues = [...localValues];

      if (localValues[index]) {
        // Clear current input
        newValues[index] = "";
        setLocalValues(newValues);
        onChange(newValues.join(""));
      } else if (index > 0) {
        // Move to previous input and clear it
        newValues[index - 1] = "";
        setLocalValues(newValues);
        onChange(newValues.join(""));
        focusInput(index - 1);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
    } else if (e.key === "ArrowRight" && index < length - 1) {
      e.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");
    const chars = pastedData.slice(0, length).split("");

    const newValues = Array(length).fill("");
    chars.forEach((char, idx) => {
      newValues[idx] = char;
    });
    setLocalValues(newValues);

    const fullValue = newValues.join("");
    onChange(fullValue);

    // Focus last filled input or first empty
    const lastFilledIndex = chars.length - 1;
    if (lastFilledIndex >= 0 && lastFilledIndex < length - 1) {
      focusInput(lastFilledIndex + 1);
    } else if (chars.length === length) {
      onComplete?.(fullValue);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: { xs: 1, sm: 1.5 },
        justifyContent: "center",
      }}
    >
      {Array.from({ length }).map((_, index) => (
        <TextField
          key={index}
          inputRef={(el) => (inputRefs.current[index] = el)}
          value={localValues[index]}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={index === 0 ? handlePaste : undefined}
          disabled={disabled}
          error={error}
          inputProps={{
            maxLength: 1,
            inputMode: "numeric",
            pattern: "[0-9]*",
            style: {
              textAlign: "center",
              fontSize: "1.5rem",
              fontWeight: 700,
              padding: "12px 0",
            },
          }}
          sx={{
            width: { xs: 45, sm: 56 },
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              backgroundColor: localValues[index]
                ? alpha(theme.palette.primary.main, 0.05)
                : "transparent",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
              "&.Mui-focused": {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 2,
                },
              },
              "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.error.main,
              },
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: localValues[index]
                ? theme.palette.primary.main
                : theme.palette.divider,
            },
          }}
        />
      ))}
    </Box>
  );
};
